/**
 * Computes image dimensions.
 *
 * @param   {File}    file  Image file.
 * @returns {object}        Image dimensions.
 */
export const getImageDimensions = async (file) => {
    return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        /**
         * Revokes the object URL and detaches listeners.
         */
        const cleanup = () => {
            URL.revokeObjectURL(url);
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
        };

        /**
         * Fires after loading image.
         */
        const onLoad = () => {
            resolve({
                width: image.width,
                height: image.height,
            });

            cleanup();
        };

        /**
         * Fires when the file cannot be decoded as an image (e.g. a corrupt
         * file). Resolve with zero dimensions so callers treat it as invalid
         * instead of awaiting a Promise that would never settle.
         */
        const onError = () => {
            resolve({
                width: 0,
                height: 0,
            });

            cleanup();
        };

        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);
        image.src = url;
    });
};

/**
 * Loads file as data URL.
 *
 * @param   {File}    file File instance.
 * @returns {Promise}      Promise instance.
 */
export const toBase64 = async (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});
